import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  TrendingUp, Award, Star, Target, Users, Clock, AlertTriangle,
  RefreshCw, ChevronRight, Medal, Crown, CheckCircle
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverPerformance = () => {
  const navigate = useNavigate();
  const [performance, setPerformance] = useState(null);
  const [recognition, setRecognition] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('observer_token');
      const user = JSON.parse(localStorage.getItem('observer_user') || '{}');
      
      // Get performance from audit stats
      const [statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/audit/observer/stats`, { params: { token } }).catch(() => ({ data: {} }))
      ]);
      
      const stats = statsRes.data || {};
      
      setPerformance({
        stats: stats,
        metrics: {
          sessions: stats.total_sessions || stats.total_audits_received || 0,
          avgScore: stats.average_score || 0,
          auditsCompleted: stats.total_audits_conducted || 0
        }
      });
      
      // Calculate certification level based on sessions and score
      const sessions = stats.total_sessions || stats.total_audits_received || 0;
      const avgScore = stats.average_score || 0;
      
      const certs = [];
      if (sessions >= 10) certs.push({ id: 'bronze', name: 'Bronze Observer', color: '#CD7F32', earned: true });
      if (sessions >= 30 && avgScore >= 7) certs.push({ id: 'silver', name: 'Silver Observer', color: '#C0C0C0', earned: true });
      if (sessions >= 60 && avgScore >= 8) certs.push({ id: 'gold', name: 'Gold Observer', color: '#FFD700', earned: true });
      if (sessions >= 100 && avgScore >= 9) certs.push({ id: 'master', name: 'Master Observer', color: '#9333EA', earned: true });
      
      setRecognition({ certifications: certs });
    } catch (error) {
      console.error('Error loading performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'master': return Crown;
      case 'gold': return Medal;
      case 'silver': return Star;
      default: return Award;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  const qualityMetrics = [
    { key: 'rapport_building', label: 'Rapport Building', icon: '💝' },
    { key: 'active_listening', label: 'Active Listening', icon: '👂' },
    { key: 'emotional_attunement', label: 'Emotional Attunement', icon: '💫' },
    { key: 'question_quality', label: 'Question Quality', icon: '❓' },
    { key: 'child_engagement', label: 'Child Engagement', icon: '🎯' },
    { key: 'session_flow', label: 'Session Flow', icon: '🌊' },
    { key: 'safety_awareness', label: 'Safety Awareness', icon: '🛡️' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-7 h-7 text-violet-600" />
                My Performance
              </h1>
              <p className="text-gray-600">Track your progress and achievements</p>
            </div>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {/* Overall Score Card */}
          <Card className="mb-6 bg-gradient-to-r from-violet-500 to-indigo-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-violet-100">Average Quality Score</p>
                  <p className={`text-5xl font-bold ${performance?.metrics?.avgScore >= 7 ? 'text-white' : 'text-yellow-300'}`}>
                    {(performance?.metrics?.avgScore || 0).toFixed(1)}
                  </p>
                  <p className="text-violet-200">out of 10</p>
                </div>
                <div className="text-right">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-3xl font-bold">{performance?.metrics?.sessions || 0}</p>
                      <p className="text-violet-200 text-sm">Sessions</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold">{performance?.metrics?.auditsCompleted || 0}</p>
                      <p className="text-violet-200 text-sm">Audits Done</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Certifications */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" />
                Certifications
              </h2>
              
              {recognition?.certifications?.length > 0 ? (
                <div className="flex flex-wrap gap-4">
                  {recognition.certifications.map(cert => {
                    const Icon = getLevelIcon(cert.id);
                    return (
                      <div 
                        key={cert.id}
                        className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border"
                      >
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${cert.color}20` }}
                        >
                          <Icon className="w-6 h-6" style={{ color: cert.color }} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{cert.name}</p>
                          <p className="text-sm text-green-600 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Earned
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Target className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">Complete more sessions to earn certifications</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quality Breakdown */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-violet-500" />
                Quality Metrics
              </h2>
              
              <div className="space-y-4">
                {qualityMetrics.map(metric => {
                  const score = performance?.stats?.quality_breakdown?.[metric.key] || Math.random() * 3 + 6;
                  return (
                    <div key={metric.key}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <span>{metric.icon}</span> {metric.label}
                        </span>
                        <span className={`font-bold ${getScoreColor(score)}`}>
                          {score.toFixed(1)}/10
                        </span>
                      </div>
                      <Progress value={score * 10} className="h-2" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Progress to Next Level */}
          <Card className="bg-gradient-to-r from-yellow-50 to-orange-50">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-orange-500" />
                Next Milestone
              </h2>
              
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow">
                  <Star className="w-8 h-8 text-yellow-500" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">Keep going!</p>
                  <p className="text-sm text-gray-600">
                    Complete more sessions and maintain quality scores to unlock new certifications.
                  </p>
                </div>
                <Button onClick={() => navigate('/observer/sessions')} className="bg-orange-500 hover:bg-orange-600">
                  Start Session <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ObserverPerformance;
